/*
 * Copyright (c) 2025 AirAsia Berhad (Malaysia). All rights reserved.
 *
 * This software is proprietary and confidential. Unauthorized copying of this file,
 * via any medium, is strictly prohibited. Do not distribute, copy, modify, or reuse
 * any part of this code without explicit written permission from AirAsia Move.
 *
 * For internal use only.
 */

import React, { createContext, useContext, useEffect } from 'react';
import type {
  HotelIntegrations,
  HotelIntegrationsProviderProps,
} from './types';
import { warnMissingHotelIntegrations } from './validateIntegrations';

type HotelIntegrationsContextType = Readonly<Partial<HotelIntegrations>>;

// Hotels owns this contract; each host supplies its Flight implementation.
const HotelIntegrationsContext = createContext<
  HotelIntegrationsContextType | undefined
>(undefined);

const HotelIntegrationsProvider = ({
  children,
  integrations,
}: HotelIntegrationsProviderProps) => {
  useEffect(() => {
    // Development-only early warning; required integrations still fail clearly
    // when the screen that needs them is rendered.
    warnMissingHotelIntegrations(integrations);
  }, [integrations]);

  // The host owns the object identity; this provider only exposes its adapters.
  return (
    <HotelIntegrationsContext.Provider value={integrations}>
      {children}
    </HotelIntegrationsContext.Provider>
  );
};

const useIntegrationContext = (): HotelIntegrationsContextType => {
  const integrations = useContext(HotelIntegrationsContext);

  // A consumer outside the provider is a host setup error.
  if (!integrations) {
    throw new Error(
      'Hotel integrations must be used inside HotelIntegrationsProvider',
    );
  }

  return integrations;
};

export const useHotelIntegration = <Key extends keyof HotelIntegrations>(
  key: Key,
): HotelIntegrations[Key] => {
  const integration = useIntegrationContext()[key];

  // Use this for booking-flow capabilities: do not silently render a broken UI.
  if (!integration) {
    throw new Error(
      `Hotel integration "${key}" is required for this feature. Provide it through HotelIntegrationsProvider.`,
    );
  }

  return integration as HotelIntegrations[Key];
};

export const useOptionalHotelIntegration = <
  Key extends keyof HotelIntegrations,
>(
  key: Key,
): HotelIntegrationsContextType[Key] => {
  // Optional integrations also support consumers rendered without a provider.
  return useContext(HotelIntegrationsContext)?.[key];
};

export default HotelIntegrationsProvider;
